import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { SearchHit } from '@/types'

import { cn } from '@/lib/utils'
import { accentClasses } from '@/constants/hubs'

interface SearchHitRowProps {
    hit: SearchHit
    active?: boolean
    onMouseEnter?: () => void
    onClick?: () => void
    className?: string
}

// One result row, shared by the header overlay and the full results page so the
// two never drift apart. `kind` implies which collection the row came from,
// which is what keeps the text inline-editable without widening the payload.
export const SearchHitRow: FC<SearchHitRowProps> = ({
    hit,
    active = false,
    onMouseEnter,
    onClick,
    className
}): ReactNode => {
    const colors = accentClasses(hit.accent)
    const isGuide = hit.kind === 'guide'

    const collection = isGuide ? 'pillars' : 'posts'
    const titleField = isGuide ? 'name' : 'title'
    const excerptField = isGuide ? 'tagline' : 'excerpt'

    return (
        <Link
            href={hit.href}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            data-active={active ? 'true' : undefined}
            className={cn(
                'flex items-start gap-4 rounded-xl p-3 transition-colors',
                active ? 'bg-secondary' : 'hover:bg-secondary/60',
                className
            )}
        >
            <span className='relative block size-14 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted'>
                {hit.coverImage && (
                    <img
                        src={hit.coverImage}
                        alt=''
                        loading='lazy'
                        className='absolute inset-0 size-full object-cover'
                    />
                )}
            </span>

            <span className='min-w-0 flex-1'>
                <span
                    className={cn(
                        'block text-[0.65rem] font-semibold uppercase tracking-[0.14em]',
                        colors.text
                    )}
                >
                    {isGuide ? 'Guide' : (hit.context ?? 'Article')}
                </span>

                <span
                    data-mdlfy-cms={`${collection}:${hit.id}:${titleField}`}
                    className='mt-1 block truncate font-heading font-semibold leading-snug text-foreground'
                >
                    {hit.title}
                </span>

                <span
                    data-mdlfy-cms={`${collection}:${hit.id}:${excerptField}`}
                    className='mt-1 line-clamp-2 block text-sm leading-relaxed text-muted-foreground'
                >
                    {hit.excerpt}
                </span>
            </span>
        </Link>
    )
}
